import { createContext, useContext } from 'react';
import type { Me } from './api';

export interface Session { me: Me; stats: Stats | null; refreshStats: () => void; refreshMe: () => void; signOut: () => void }
export interface Stats {
  parties: Record<string, number>;
  review_queue: number;
  my_approvals: number;
  my_exceptions?: number;
  inbox?: number;
  agent_tasks: Record<string, number>;
  proposals_today: Record<string, number>;
  team_attention?: number;
  today?: Record<'leads' | 'replies' | 'deals_moved' | 'quotes_sent', { n: number; by_agents: number }>;
}

export const SessionCtx = createContext<Session | null>(null);
export function useSession(): Session {
  const s = useContext(SessionCtx);
  if (!s) throw new Error('useSession outside the signed-in app');
  return s;
}
export const isAdmin = (me: Me) => me.role === 'owner' || me.role === 'admin';
// Mirrors hasScope in server/src/rbac: verb:* and *:* are wildcards, and no wildcard covers operate.
export const scopeCovers = (granted: string[], required: string) => {
  const [verb, domain] = required.split(':');
  return granted.some((g) => { const [gv, gd] = g.split(':'); return ((gv === '*' && verb !== 'operate') || gv === verb) && (gd === '*' || gd === domain); });
};
// What a person's role lets them do (ADR 0029); a screen they cannot read is hidden.
export const can = (me: Me, scope?: string) => !scope || !me.scopes || scopeCovers(me.scopes, scope);
// The read (or read and propose) part of a set of scopes, for a token or key no wider than its holder.
export const accessScopes = (scopes: string[], access: 'read' | 'propose') => [...new Set(scopes.flatMap((s) => {
  const [verb, domain] = s.split(':');
  const verbs = access === 'read' ? ['read'] : ['read', 'propose'];
  return verb === '*' ? verbs.map((v) => `${v}:${domain}`) : verbs.includes(verb!) ? [s] : [];
}))];
// The operator sets which features a workspace's plan includes; a screen of one it does not include is hidden.
export const hasFeature = (me: Me, feature?: string) => !feature || !me.features || me.features.includes(feature);
// The pieces of the app agents bring (docs/plans/agent-ui-plan.md): one shows only while a hired agent
// that is not retired brings it. The server keeps the same list (UI_SURFACES in server/src/packs).
export const SURFACES = ['help', 'inbox_ask', 'coach', 'agent_builder', 'workspace_designer', 'workflow_designer', 'setup', 'day_plan', 'okr_map', 'my_okrs', 'connections'] as const;
export type Surface = (typeof SURFACES)[number];
export const hasSurface = (me: Me, surface: Surface) => !!me.surfaces?.includes(surface);
